import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import SlotUploader from "../SlotUploader";
import { Collapse } from "../calm";
import { filesOfSlot } from "../onboardingModel";
import { ButtonTip } from "@/components/ui/button-tip";
import { StepBody, StepFooter, StepHeading } from "../StepCard";
import { numberedCount, optionalSlots } from "../steps";
import { PersonalDataNotice, slotDescription, slotTitle } from "./SlotStep";
import type { StepContext } from "./shared";

/** Step 6: all the optional slots on one quiet page. "Überspringen" is as visible as "Weiter". */
export default function OptionalStep({ ctx }: { ctx: StepContext }) {
  const { c } = ctx;
  const { t } = useTranslation();
  const view = c.view!;
  const slots = optionalSlots(view);
  const total = slots.reduce((sum, slot) => sum + filesOfSlot(view.files, slot.slot).length, 0);
  const running = c.uploads.some((item) => slots.some((slot) => slot.slot === item.slot) && (item.status === "waiting" || item.status === "uploading"));

  return (
    <>
      <StepBody>
        <StepHeading
          eyebrow={t("onboardingPage.nav.stepOf", { current: ctx.step.index, total: numberedCount(ctx.steps) })}
          title={t("onboardingPage.titles.optional")}
          lead={t("onboardingPage.optional.lead")}
          focus={ctx.focus}
          extra={<span className="inline-flex rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">{t("onboardingPage.optional.badge")}</span>}
        />
        <Collapse open={!!ctx.nudge}>
          <p role="status" className="rounded-xl bg-muted/60 px-4 py-3 text-sm">{ctx.nudge}</p>
        </Collapse>
        <PersonalDataNotice />
        <div className="grid gap-3 sm:grid-cols-2">
          {slots.map((slot) => (
            <SlotUploader
              key={slot.slot}
              slot={slot}
              compact
              title={slotTitle(t, slot)}
              description={slotDescription(t, slot)}
              stored={filesOfSlot(view.files, slot.slot)}
              items={c.uploads.filter((item) => item.slot === slot.slot)}
              canEdit={view.canEdit}
              notice={c.slotNotices[slot.slot]}
              errorText={c.errorText}
              onPick={(files) => c.addFiles(slot, files)}
              onRemove={(file) => void c.removeStored(file)}
              preview={c.preview}
              onRetry={c.retryUpload}
              onDismiss={(item) => c.dropUpload(item.id)}
              onDismissNotice={() => c.setSlotNotices((previous) => ({ ...previous, [slot.slot]: undefined }))}
              onSkipped={(count) => c.setSlotNotices((previous) => ({ ...previous, [slot.slot]: t("onboardingPage.drop.skippedFolder", { count }) }))}
            />
          ))}
        </div>
      </StepBody>
      <StepFooter
        onBack={ctx.back}
        onNext={ctx.next}
        nextDisabled={running}
        nextReason={t("onboardingPage.tips.waitUploads")}
        hint={running ? t("onboardingPage.nav.waitUploads") : null}
        secondary={
          total === 0 ? (
            <ButtonTip label={t("onboardingPage.tips.skip")}>
              <Button type="button" variant="outline" onClick={ctx.next}>
                {t("onboardingPage.nav.skip")}
              </Button>
            </ButtonTip>
          ) : null
        }
      />
    </>
  );
}
